export interface ImageCompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number;
  mimeType?: string;
}

const DEFAULT_OPTIONS: ImageCompressionOptions = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
  mimeType: 'image/jpeg'
};

export const compressImage = (
  file: File,
  options: ImageCompressionOptions = {}
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const opts = { ...DEFAULT_OPTIONS, ...options };

    const img = new Image();
    const reader = new FileReader();

    reader.onload = (e) => {
      const result = e.target?.result as string;
      img.src = result;
    };

    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        let { width, height } = img;

        if (width > (opts.maxWidth || 1920) || height > (opts.maxHeight || 1920)) {
          const ratio = Math.min(
            (opts.maxWidth || 1920) / width,
            (opts.maxHeight || 1920) / height
          );
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Failed to get canvas context'));
          return;
        }

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, width, height);
        ctx.drawImage(img, 0, 0, width, height);

        const compressedDataUrl = canvas.toDataURL(
          opts.mimeType || 'image/jpeg',
          opts.quality || 0.8
        );

        resolve(compressedDataUrl);
      } catch (error) {
        reject(error);
      }
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    reader.readAsDataURL(file);
  });
};

export const compressProductImage = (file: File): Promise<string> => {
  return compressImage(file, {
    maxWidth: 800,
    maxHeight: 800,
    quality: 0.2,
    mimeType: 'image/jpeg'
  });
};

export const compressProductGalleryImage = (file: File): Promise<string> => {
  return compressImage(file, {
    maxWidth: 1200,
    maxHeight: 1200,
    quality: 0.2,
    mimeType: 'image/jpeg'
  });
};

export const compressThumbnail = (file: File): Promise<string> => {
  return compressImage(file, {
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.2,
    mimeType: 'image/jpeg'
  });
};
